import React, { useMemo, useRef } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const utahData = "/img/UTDT_Logo.png";
const kpi = "/img/KPI-DASH 2.png";
const bodex = "/img/bodexLogo.png";
const pdfIcon = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

const numberFmt = (n) => (Number(n) || 0).toLocaleString("en-US");

export default function AeroTable1({ data = [] }) {
  const pdfRef = useRef(null);

  const computed = useMemo(() => {
    if (!data || data.length === 0) return null;

    // years from keys Female-YYYY
    const years = Object.keys(data[0] || {})
      .filter((k) => k.includes("Female-"))
      .map((k) => k.split("-")[1])
      .sort((a, b) => Number(a) - Number(b));

    const tableHeaders = [
      "Age Group",
      ...years.flatMap((y) => [`Female-${y}`, `Male-${y}`]),
    ];

    const ageGroups = [...new Set(data.map((r) => r["Age Group"]))];

    // X labels: Female (2018), Male (2018), ...
    const chartLabels = years.flatMap((year) =>
      ["Female", "Male"].map((g) => `${g} (${year})`),
    );

    const predefinedColors = [
      "#FFA1B5",
      "#86C7F3",
      "#FFE29A",
      "#93D9D9",
      "#C2A3FF",
      "#FFC58C",
      "#B9D2AA",
      "#6794D1",
      "rgba(1,76,178,0.6)",
    ];

    const datasets = ageGroups.map((ageGroup, index) => ({
      label: ageGroup,
      data: years.flatMap((year) =>
        ["Female", "Male"].map((gender) => {
          const fieldName = `${gender}-${year}`;
          const row = data.find((r) => r["Age Group"] === ageGroup);
          return row ? Number(row[fieldName]) || 0 : 0;
        }),
      ),
      backgroundColor: predefinedColors[index % predefinedColors.length],
      borderWidth: 0,
    }));

    // --------- Summary Stats (like your Construction page) ----------
    const lastYear = years[years.length - 1];
    const firstYear = years[0];

    const sumBy = (year, gender) =>
      data.reduce(
        (acc, row) => acc + (Number(row[`${gender}-${year}`]) || 0),
        0,
      );

    const totalFirstYear =
      sumBy(firstYear, "Female") + sumBy(firstYear, "Male");
    const totalLastYear = sumBy(lastYear, "Female") + sumBy(lastYear, "Male");

    const growthPercent =
      totalFirstYear > 0
        ? (((totalLastYear - totalFirstYear) / totalFirstYear) * 100).toFixed(1)
        : "0.0";

    // peak year based on total female+male across age groups
    const totalsByYear = years.map((y) => ({
      year: y,
      total: sumBy(y, "Female") + sumBy(y, "Male"),
    }));
    const peak = totalsByYear.reduce(
      (best, cur) => (cur.total > best.total ? cur : best),
      totalsByYear[0],
    );

    // current (last year) gender split
    const femaleLast = sumBy(lastYear, "Female");
    const maleLast = sumBy(lastYear, "Male");

    return {
      years,
      tableHeaders,
      ageGroups,
      chartLabels,
      datasets,
      firstYear,
      lastYear,
      totalFirstYear,
      totalLastYear,
      growthPercent,
      peakYear: peak?.year,
      peakTotal: peak?.total,
      femaleLast,
      maleLast,
    };
  }, [data]);

  if (!data || data.length === 0) return <p>No Data Available</p>;
  if (!computed) return <p>No Data Available</p>;

  const chartData = {
    labels: computed.chartLabels,
    datasets: computed.datasets.filter(
      (ds) => (ds.label || "").toLowerCase() !== "total",
    ),
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: { font: { size: window.innerWidth < 768 ? 10 : 12 } },
      },
      title: {
        display: true,
        text: "Employment by Age Group, Gender, and Year",
        font: {
          size: window.innerWidth < 768 ? 14 : 16,
          weight: "bold",
        },
      },
      tooltip: {
        callbacks: {
          label: (ctx) =>
            `${ctx.dataset.label}: ${numberFmt(ctx.raw)} (${ctx.label})`,
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        title: {
          display: true,
          text: "Gender and Year",
          font: {
            size: window.innerWidth < 768 ? 12 : 14,
            weight: "bold",
          },
        },
        ticks: {
          maxRotation: window.innerWidth < 768 ? 90 : 45,
          minRotation: 0,
          font: { size: window.innerWidth < 768 ? 8 : 10 },
        },
        grid: { display: false },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        title: {
          display: true,
          text: "Employment Count",
          font: {
            size: window.innerWidth < 768 ? 12 : 14,
            weight: "bold",
          },
        },
        ticks: {
          font: { size: window.innerWidth < 768 ? 8 : 10 },
          callback: (v) => numberFmt(v),
        },
        grid: { display: true },
      },
    },
  };

   const downloadPDF = () => {
     const input = pdfRef.current;
     html2canvas(input, {
       scale: 2,
       useCORS: true,
       backgroundColor: "#ffffff",
       scrollY: -window.scrollY,
     }).then((canvas) => {
       const imgData = canvas.toDataURL("image/png");
       const pdf = new jsPDF("p", "mm", "a4");
       const pageWidth = pdf.internal.pageSize.getWidth();
       const pageHeight = pdf.internal.pageSize.getHeight();
       const imgWidth = pageWidth - 10;
       const imgHeight = (canvas.height * imgWidth) / canvas.width;

       let heightLeft = imgHeight;
       let position = 0;
       let pageNumber = 1;

       const addHeader = () => {
         const logoWidth = 20; // ideal header width
         const logoHeight = 20; // adjust ratio if needed

         pdf.addImage(utahData, "PNG", 70, 10, logoWidth, logoHeight);
         pdf.setFontSize(26);
         pdf.text("UTahData.org", 95, 22);
         pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
         pdf.line(10, 35, 200, 35);
       };

       const addFooter = (pageNumber) => {
         pdf.setFontSize(9);
         const footerY = pageHeight - 10;
         pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
         pdf.text(
           `Downloaded on: ${new Date().toLocaleDateString()}`,
           10,
           footerY,
         );
         pdf.text(
           `Page ${pageNumber}`,
           pageWidth - 10,
           footerY,
           null,
           null,
           "right",
         );
       };

       // First page
       addHeader();
       pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
       heightLeft -= pageHeight - 32;

       // Add more pages if needed
       while (heightLeft > 0) {
         pdf.addPage();
         pageNumber++;
         addHeader();
         position = heightLeft - imgHeight;
         pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
         heightLeft -= pageHeight - 32;
         addFooter(pageNumber);
       }

       // Final footer
       // ===== FINAL FOOTER SAFE AREA =====

       const footerTopY = pageHeight - 40; // start footer 40mm from bottom
       const footerContentY = pageHeight - 28; // logos row
       const footerBottomTextY = pageHeight - 10; // bottom text row

       // Top Divider Line
       pdf.line(10, footerTopY, pageWidth - 10, footerTopY);

       // UtahData logo
       const utahWidth = 18;
       const utahHeight = 18;
       pdf.addImage(
         utahData,
         "PNG",
         20,
         footerContentY - 8,
         utahWidth,
         utahHeight,
       );

       // Built by
       pdf.setFontSize(10);
       pdf.text("Built by", 45, footerContentY);

       // BODEX logo
       const bodexWidth = 36;
       const bodexHeight = bodexWidth * 0.3;
       pdf.addImage(
         bodex,
         "PNG",
         60,
         footerContentY - 6,
         bodexWidth,
         bodexHeight,
       );

       // Using text
       pdf.text("using", 102, footerContentY);

       // KPI logo
       pdf.addImage(kpi, "PNG", 118, footerContentY - 8, 16, 16);

       // Version
       pdf.text("20250916.v3.198", 140, footerContentY);

       // Bottom Info Line
       pdf.setFontSize(8);
       pdf.text(`From: ${window.location.href}`, 10, footerBottomTextY);
       pdf.text(
         `Downloaded on: ${new Date().toLocaleDateString()}`,
         pageWidth - 10,
         footerBottomTextY,
         null,
         null,
         "right",
       );
       pdf.save("aerospace_employment_by_age_gender.pdf");
     });
   };

  return (
    <>
      <div
        ref={pdfRef}
        className="p-6 bg-white rounded-xl shadow-md"
        style={{
          background:
            "linear-gradient(135deg, rgba(239,246,255,1) 0%, rgba(255,255,255,1) 45%, rgba(253,242,248,1) 100%)",
        }}
      >
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Employment Data by Age & Gender ({computed.firstYear}–
          {computed.lastYear})
        </h2>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Overall Growth ({computed.firstYear}–{computed.lastYear})
            </p>
            <p className="text-xl font-bold text-blue-800">
              {Number(computed.growthPercent) >= 0 ? "+" : ""}
              {computed.growthPercent}%
            </p>
          </div>

          <div className="p-4 bg-yellow-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">Peak Year</p>
            <p className="text-xl font-bold text-yellow-800">
              {computed.peakYear}
            </p>
            <p className="text-xs text-gray-600 mt-1">
              Total: {numberFmt(computed.peakTotal)}
            </p>
          </div>

          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              {computed.lastYear} Total Employment
            </p>
            <p className="text-xl font-bold text-green-800">
              {numberFmt(computed.totalLastYear)}
            </p>
            <p className="text-xs text-gray-600 mt-1">
              Female: {numberFmt(computed.femaleLast)} · Male:{" "}
              {numberFmt(computed.maleLast)}
            </p>
          </div>
        </div>

        {/* Table + Chart */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Table */}
          <div className="flex flex-col justify-center">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    {computed.tableHeaders.map((h) => (
                      <th
                        key={h}
                        className={`p-2 ${h === "Age Group" ? "text-left" : ""}`}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {data.map((row, rowIndex) => (
                    <tr
                      key={rowIndex}
                      className={`hover:bg-gray-50 ${
                        rowIndex % 2 === 0 ? "bg-white" : "bg-gray-50/60"
                      }`}
                    >
                      <td className="p-2 text-left font-medium">
                        {row["Age Group"]}
                      </td>

                      {computed.years.flatMap((year) => [
                        <td
                          key={`female-${year}-${rowIndex}`}
                          className="p-2"
                          style={{
                            textAlign: "right",
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {numberFmt(row[`Female-${year}`])}
                        </td>,
                        <td
                          key={`male-${year}-${rowIndex}`}
                          className="p-2"
                          style={{
                            textAlign: "right",
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {numberFmt(row[`Male-${year}`])}
                        </td>,
                      ])}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-xs text-gray-500 mt-3">
              Tip: Scroll horizontally to view all years.
            </p>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[520px] w-full h-96 p-4">
              <Bar data={chartData} options={chartOptions} />
            </div>
          </div>
        </div>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdfIcon} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
}
